export const BUCKET_INTERVAL = {
  FIFTEEN_MINUTES: "15 minutes",
  ONE_HOUR: "1 hour",
  FOUR_HOURS: "4 hours",
  ONE_DAY: "1 day",
} as const

export type BucketInterval =
  (typeof BUCKET_INTERVAL)[keyof typeof BUCKET_INTERVAL]

export function getPostgreSQLBucketExpression(
  frontendBucketInterval: BucketInterval,
  timezone = "UTC",
  timestampColumn = "run_occurred_at",
): string {
  const tzColumn =
    timezone === "UTC"
      ? timestampColumn
      : `${timestampColumn} AT TIME ZONE '${timezone}'`

  switch (frontendBucketInterval) {
    case BUCKET_INTERVAL.FIFTEEN_MINUTES:
      if (timezone === "UTC") {
        return `date_bin('15 minutes'::interval, ${timestampColumn}, '2000-01-01'::timestamp)`
      }
      return `(date_bin('15 minutes'::interval, ${tzColumn}, '2000-01-01'::timestamp) AT TIME ZONE '${timezone}')::timestamptz`

    case BUCKET_INTERVAL.FOUR_HOURS:
      if (timezone === "UTC") {
        return `date_bin('4 hours'::interval, ${timestampColumn}, '2000-01-01'::timestamp)`
      }
      return `(date_bin('4 hours'::interval, ${tzColumn}, '2000-01-01'::timestamp) AT TIME ZONE '${timezone}')::timestamptz`

    case BUCKET_INTERVAL.ONE_DAY:
      if (timezone === "UTC") {
        return `date_trunc('day', ${timestampColumn})`
      }
      return `(date_trunc('day', ${tzColumn}) AT TIME ZONE '${timezone}')::timestamptz`

    case BUCKET_INTERVAL.ONE_HOUR:
    default:
      if (timezone === "UTC") {
        return `date_trunc('hour', ${timestampColumn})`
      }
      return `(date_trunc('hour', ${tzColumn}) AT TIME ZONE '${timezone}')::timestamptz`
  }
}

export function isValidBucketInterval(bucketInterval: string): boolean {
  const validIntervals: readonly string[] = Object.values(BUCKET_INTERVAL)
  return validIntervals.includes(bucketInterval)
}

export function getDefaultBucketInterval(): string {
  return BUCKET_INTERVAL.FOUR_HOURS
}

export function calculateBucketEndTime(
  bucketStartTime: string | Date,
  bucketInterval: BucketInterval = BUCKET_INTERVAL.FOUR_HOURS,
): string {
  const startDate =
    typeof bucketStartTime === "string"
      ? new Date(bucketStartTime)
      : bucketStartTime

  let endDate: Date

  switch (bucketInterval) {
    case BUCKET_INTERVAL.FIFTEEN_MINUTES:
      endDate = new Date(startDate.getTime() + 15 * 60 * 1000)
      break
    case BUCKET_INTERVAL.ONE_HOUR:
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000)
      break
    case BUCKET_INTERVAL.FOUR_HOURS:
      endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000)
      break
    case BUCKET_INTERVAL.ONE_DAY:
      endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000)
      break
    default:
      endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000)
  }

  return endDate.toISOString()
}
